import { useMemo } from 'react';
import { Badge, NavLink, ScrollArea } from '@buildex/ui';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { MenuItem, ModuleManifest } from '@buildex/module-contract';

interface SidebarProps {
  /** Меню строится из манифестов активных модулей (§5) */
  manifests: ModuleManifest[];
}

interface FlatItem {
  item: MenuItem;
  moduleIcon: ModuleManifest['icon'];
  order: number;
}

function HomeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}

/**
 * Пункты всех модулей — в один список по шагам user flow (MenuItem.order);
 * без order — в конец в порядке загрузки модулей. Модули, не подключённые
 * тенанту, в манифестах отсутствуют, поэтому их шаги просто не показываются.
 */
function flatten(manifests: ModuleManifest[]): FlatItem[] {
  const items: FlatItem[] = [];
  manifests.forEach((manifest, moduleIndex) => {
    manifest.menuItems.forEach((item, itemIndex) => {
      items.push({
        item,
        moduleIcon: manifest.icon,
        order: item.order ?? 1000 + moduleIndex * 100 + itemIndex,
      });
    });
  });
  return items.sort((a, b) => a.order - b.order);
}

function StepBadge({ step }: { step: string }) {
  return (
    <Badge circle size="lg" variant="light" fw={700}>
      {step}
    </Badge>
  );
}

export function Sidebar({ manifests }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const items = useMemo(() => flatten(manifests), [manifests]);
  const location = pathname + search;
  // Если хоть один пункт совпал по activePattern — активны только такие пункты
  const patternMatched = items.some(({ item }) => item.activePattern?.test(location) === true);
  const isActive = (item: MenuItem): boolean => {
    if (item.activePattern !== undefined) return item.activePattern.test(location);
    if (patternMatched) return false;
    return pathname === item.path || pathname.startsWith(`${item.path}/`);
  };

  return (
    <ScrollArea p="xs">
      <NavLink
        label={t('home.menu')}
        leftSection={<HomeIcon />}
        active={pathname === '/'}
        onClick={() => navigate('/')}
        mb={4}
      />
      {items.map(({ item, moduleIcon: Icon }) => {
        const isSubStep = item.step !== undefined && item.step.includes('.');
        return (
          <NavLink
            key={item.path}
            label={t(item.label)}
            leftSection={
              item.step !== undefined ? (
                <StepBadge step={item.step} />
              ) : item.icon ? (
                <item.icon />
              ) : (
                <Icon />
              )
            }
            pl={isSubStep ? 'xl' : undefined}
            active={isActive(item)}
            onClick={() => navigate(item.path)}
          >
            {item.children?.map((child) => (
              <NavLink
                key={child.path}
                label={t(child.label)}
                active={pathname === child.path}
                onClick={() => navigate(child.path)}
              />
            ))}
          </NavLink>
        );
      })}
    </ScrollArea>
  );
}
